import { useEffect, useRef, useState } from 'react'
import { Accordion, Modal } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { checkPersonSocSec } from '../../../actions/persons/personsocseccheck'
import { personGet, personTeamsGet } from '../../../api'
import { Button } from '../../../common/Button.tsx'
import { Confirmation } from '../../../common/confirmation'
import { DuplicateSocSecModal } from '../../../common/DuplicateSocSecModal.jsx'
import { IntegrationWarningModal } from '../../../common/IntegrationWarningModal.jsx'
import { validateActiveIntegrations } from '../../../common/integrationValidation'
import { LoadingButton } from '../../../common/loaders/loadingbutton'
import { findPersonModalFormErrors } from '../../../common/person/personmodalvalidation'
import { activeIntegrationsSelector, authSelector, socSecCheckSelector } from '../../../reducers'
import { ContractGroup1 } from './contractgroup1'
import { ContractGroup2 } from './contractgroup2'
import { ContractGroup3 } from './contractgroup3'
import { ContractGroup4 } from './contractgroup4'
import { ContractGroup5 } from './contractgroup5'
import { ContractGroup6 } from './contractgroup6'

export function ContractModalBody({
	state,
	dispatch,
	title,
	show,
	handleSave,
	handleCancel,
	handleLock,
	checkPersonNumber,
	personNumberCheckResult,
	actionType,
	onFormErrors,
	setPdf,
}) {
	const isLocked = state.status === 'COMPLETE'

	const { t } = useTranslation()
	const reduxDispatch = useDispatch()

	const formMethods = useForm() // validation { mode: "onChange" }

	const auth = useSelector(authSelector)
	const socSecCheckResult = useSelector(socSecCheckSelector)
	const isTemplateContract = state.status === 'MODEL'

	const [formErrors, setFormErrors] = useState({})
	const [checkingPersonNumber, setCheckingPersonNumber] = useState(false)
	const [submitForm, setSubmitForm] = useState(false)
	const [submitLock, setSubmitLock] = useState(false)
	const [showIntegrationConfirm, setShowIntegrationConfirm] = useState(false)
	const [integrationErrors, setIntegrationErrors] = useState([])
	const [pendingAction, setPendingAction] = useState(null)
	const [socSecDuplicateWarning, setSocSecDuplicateWarning] = useState(false)

	const fieldTypingTimersRef = useRef({})
	const typingDelay = 200

	useEffect(() => {
		if (show) {
			Object.keys(fieldTypingTimersRef.current).forEach((key) => {
				clearTimeout(fieldTypingTimersRef.current[key])
			})
			setFormErrors({})
			setSubmitForm(false)
			setSubmitLock(false)
			setCheckingPersonNumber(false)
			setShowIntegrationConfirm(false)
			setIntegrationErrors([])
			setPendingAction(null)
			setSocSecDuplicateWarning(false)
		}
	}, [show])

	useEffect(() => {
		let isCancelled = false

		const loadPersonTeams = async () => {
			if (!show || !state.personId) {
				return
			}

			const existingTeams = state.person?.controlDetails?.teams || state.person?.teams
			if (Array.isArray(existingTeams) && existingTeams.length > 0) {
				return
			}

			try {
				const response = await personTeamsGet(state.personId)
				const teams = response?.data?.assignedTeams || []

				if (!isCancelled) {
					dispatch({ type: 'set_person_teams', payload: teams })
				}
			} catch (error) {
				console.error('Failed to fetch person teams for contract modal', error)
			}
		}

		loadPersonTeams()

		return () => {
			isCancelled = true
		}
	}, [show, state.personId, state.person?.teams, state.person?.controlDetails?.teams, dispatch])

	const activeIntegrations = useSelector(activeIntegrationsSelector) || {}

	const getIntegrationValidationPerson = (sourcePerson, teams) => {
		const person = state.person || {}
		const source = sourcePerson || person
		const personControlDetails = source.controlDetails || person.controlDetails || {}
		const personEmploymentDetails = source.employmentDetails || person.employmentDetails || {}
		const personSalaryDetails = source.salaryDetails || person.salaryDetails || {}
		const personOtherDetails = source.otherDetails || person.otherDetails || {}
		const personDetails = source.details || person.details || {}
		const personTeams =
			teams || personControlDetails.teams || personControlDetails.teamMemberships || source.teams || person.teams || []

		return {
			...person,
			details: {
				...personDetails,
				iban: person.iban ?? personDetails.iban ?? '',
			},
			otherDetails: {
				...personOtherDetails,
				email: personOtherDetails.email ?? person.email ?? '',
			},
			employmentDetails: {
				...personEmploymentDetails,
				startDate: person.startDate || state.details?.startDate || personEmploymentDetails.startDate,
				fixedDuration: state.details?.fixedDuration ?? personEmploymentDetails.fixedDuration,
				cbaCode: state.details?.cbaCode || personEmploymentDetails.cbaCode || person.cbaCode,
				profession: state.details?.jobTitle || personEmploymentDetails.profession || person.profession,
			},
			salaryDetails: {
				...personSalaryDetails,
				formOfSalary: state.pay?.formOfSalary ?? personSalaryDetails.formOfSalary,
			},
			controlDetails: {
				...personControlDetails,
				payGroup: personControlDetails.payGroup || person.payGroup,
				teams: personTeams,
			},
		}
	}

	const checkIntegrationWarnings = async (action) => {
		if (isTemplateContract) {
			return false
		}

		let sourcePerson
		let teams
		if (state.personId) {
			try {
				const [personResp, teamsResp] = await Promise.all([personGet(state.personId), personTeamsGet(state.personId)])
				sourcePerson = personResp?.data
				teams = teamsResp?.data?.assignedTeams || []
			} catch (error) {
				console.error('Failed to load person data for integration validation', error)
			}
		}

		const phase = action === 'lock' ? 'activation' : 'draftSave'
		const errors = validateActiveIntegrations(
			getIntegrationValidationPerson(sourcePerson, teams),
			activeIntegrations,
			phase,
		)
		if (errors.length > 0) {
			setIntegrationErrors(errors)
			setPendingAction(action)
			setShowIntegrationConfirm(true)
			return true
		}
		return false
	}

	const hideIntegrationModal = () => {
		setShowIntegrationConfirm(false)
		setPendingAction(null)
		setIntegrationErrors([])
	}

	const executePendingAction = () => {
		if (pendingAction === 'save') {
			setSubmitForm(true)
			handleSave()
		} else if (pendingAction === 'lock') {
			setSubmitLock(true)
			handleLock()
		}

		setShowIntegrationConfirm(false)
		setPendingAction(null)
		setIntegrationErrors([])
	}

	function handleSubmitFinal(nrResult) {
		if (!state.newModel && !state.modelName) {
			const newErrors = findPersonModalFormErrors(t, null, null, state.person)

			if (nrResult?.exists) {
				if (!state.personId || (state.personId && state.personId !== personNumberCheckResult.existsPersonId)) {
					newErrors.nr = t('PersonNumberAlreadyExists', 'Henkilönumero on jo käytössä. Seuraava vapaa on {{nextNr}}', {
						nextNr: personNumberCheckResult.nextNr,
					})
				}
			}

			if (Object.keys(newErrors).length > 0) {
				setSubmitForm(false)
				setFormErrors(newErrors)
				onFormErrors(newErrors)
				return
			}
		}

		if (state.newPerson && state.person?.socSec) {
			reduxDispatch(checkPersonSocSec(state.person.socSec))
			return
		}

		proceedToSave()
	}

	async function proceedToSave() {
		if (await checkIntegrationWarnings('save')) {
			setSubmitForm(false)
			return
		}

		handleSave()
	}

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional — this effect is triggered only by actionType changes
	useEffect(() => {
		if (checkingPersonNumber) {
			if (actionType === 'CHECK_PERSON_NUMBER_SUCCEEDED') {
				setCheckingPersonNumber(false)
				if (submitForm) {
					handleSubmitFinal(personNumberCheckResult)
				} else {
					if (personNumberCheckResult?.exists) {
						if (!state.personId || (state.personId && state.personId !== personNumberCheckResult.existsPersonId)) {
							setFormErrors({
								...formErrors,
								nr: t('PersonNumberAlreadyExists', 'Henkilönumero on jo käytössä. Seuraava vapaa on {{nextNr}}', {
									nextNr: personNumberCheckResult.nextNr,
								}),
							})
						}
					}
				}
			}
			if (actionType === 'CHECK_PERSON_NUMBER_FAIL') {
				setCheckingPersonNumber(false)
				setSubmitForm(false)
			}
		}
		if (actionType === 'CHECK_SOC_SEC_SUCCEEDED' && submitForm) {
			if (socSecCheckResult?.exists && !socSecDuplicateWarning) {
				setSocSecDuplicateWarning(true)
				setSubmitForm(false)
			} else {
				setSocSecDuplicateWarning(false)
				proceedToSave()
			}
		}
		if (submitForm) {
			if (
				actionType === 'POST_PERSON_FAIL' ||
				actionType === 'POST_PERSON_EDIT_FAIL' ||
				actionType === 'POST_CONTRACT_FAIL' ||
				actionType === 'POST_CONTRACT_EDIT_FAIL' ||
				actionType === 'ALERT'
			) {
				setSubmitForm(false)
			}
		}
		if (submitLock) {
			if (
				actionType === 'POST_CONTRACT_COMPLETE_FAIL' ||
				actionType === 'POST_CONTRACT_REOPEN_FAIL' ||
				actionType === 'ALERT'
			) {
				setSubmitLock(false)
			}
		}
	}, [actionType])

	const handleValidation = (e) => {
		const newErrors = findPersonModalFormErrors(t, e, formErrors, state.person)
		setFormErrors(newErrors)
		if (e?.target && e.target.id === 'socSec') {
			setSocSecDuplicateWarning(false)
		}
		if (
			e?.target &&
			e.target.id === 'nr' &&
			e.target.value > 0 &&
			e.target.value.length < 20 &&
			e.target.value !== ''
		) {
			setCheckingPersonNumber(true)
			checkPersonNumber(e.target.value)
		}
	}

	const handleChange = (e) => {
		const {
			target: { id, value, checked, type },
		} = e
		if (type === 'checkbox') {
			// do not validate date fields
			return dispatch({ type: id, payload: checked })
		}
		if (type === 'date') {
			// do not validate date fields
			return dispatch({ type: id, payload: value })
		}
		if (fieldTypingTimersRef.current[id]) {
			clearTimeout(fieldTypingTimersRef.current[id])
		}

		fieldTypingTimersRef.current[id] = setTimeout(() => {
			handleValidation(e)
			dispatch({ type: id, payload: value, formMethods })
		}, typingDelay)
	}

	const handleSubmit = () => {
		setSubmitForm(true)
		if (!state.newModel && !state.modelName) {
			// wait for possible typing timers to clear
			setTimeout(() => {
				if (state.person.nr > 0 && state.person.nr !== '') {
					setCheckingPersonNumber(true)
					checkPersonNumber(state.person.nr)
				} else {
					handleSubmitFinal()
				}
			})
		} else {
			handleSubmitFinal()
		}
	}

	const callHandleLock = async () => {
		if (await checkIntegrationWarnings('lock')) {
			setSubmitLock(false)
			return
		}

		setSubmitLock(true)
		handleLock()
	}

	return (
		<>
			<Modal show={show} onHide={handleCancel} backdrop="static" size="xl" fullscreen="lg-down" scrollable>
				<Modal.Header closeButton onHide={handleCancel}>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>
				<FormProvider {...formMethods}>
					<Modal.Body className="heeros-background" as="form">
						<Accordion defaultActiveKey={['contract-1']} flush alwaysOpen>
							<Accordion.Item key="contract-1" eventKey="contract-1">
								{(state.newModel || state.modelName) && (
									<Accordion.Header>{t('ModelInfo', 'Mallin tiedot')}</Accordion.Header>
								)}
								{!state.newModel && !state.modelName && (
									<Accordion.Header>{t('PersonData', 'Henkilötiedot')}</Accordion.Header>
								)}
								<Accordion.Body>
									<ContractGroup1 state={state} dispatch={dispatch} onChange={handleChange} formErrors={formErrors} />
								</Accordion.Body>
							</Accordion.Item>
							<Accordion.Item key="contract-2" eventKey="contract-2">
								<Accordion.Header>{t('WorkContract', 'Työsopimus')}</Accordion.Header>
								<Accordion.Body>
									<ContractGroup2 state={state} dispatch={dispatch} onChange={handleChange} formErrors={formErrors} />
								</Accordion.Body>
							</Accordion.Item>

							{auth.salary && (
								<Accordion.Item key="contract-3" eventKey="contract-3">
									<Accordion.Header>{t('Basis', 'Palkan perusteet')}</Accordion.Header>
									<Accordion.Body>
										<ContractGroup3
											state={state.pay ? state : { ...state, pay: {} }}
											dispatch={dispatch}
											onChange={handleChange}
											formErrors={formErrors}
										/>
									</Accordion.Body>
								</Accordion.Item>
							)}

							<Accordion.Item key="contract-4" eventKey="contract-4">
								<Accordion.Header>{t('OtherTerms', 'Muut ehdot')}</Accordion.Header>
								<Accordion.Body>
									<ContractGroup4 state={state} dispatch={dispatch} onChange={handleChange} formErrors={formErrors} />
								</Accordion.Body>
							</Accordion.Item>
							<Accordion.Item key="contract-6" eventKey="contract-6">
								<Accordion.Header>{t('Signature', 'Allekirjoitus')}</Accordion.Header>
								<Accordion.Body>
									<ContractGroup6 state={state} dispatch={dispatch} onChange={handleChange} formErrors={formErrors} />
								</Accordion.Body>
							</Accordion.Item>

							{auth.contractAttachments && (
								<Accordion.Item key="contract-5" eventKey="contract-5">
									<Accordion.Header>{t('Attachments', 'Liitteet')}</Accordion.Header>
									<Accordion.Body>
										<ContractGroup5 state={state} />
									</Accordion.Body>
								</Accordion.Item>
							)}
						</Accordion>
					</Modal.Body>
				</FormProvider>
				<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
					<Button variant="cancel" onClick={handleCancel}>
						{isLocked ? t('Close', 'Sulje') : t('Cancel', 'Peru')}
					</Button>
					<div style={{ display: 'flex', gap: '8px' }}>
						{handleLock && !state.modelName && (
							<Confirmation
								title={
									isLocked ? t('UnlockContract', 'Avaa lukitus') : t('LockContract', 'Tallenna ja lukitse sopimus')
								}
								actionFunction={callHandleLock}
							>
								<Button
									className="me-auto"
									key="key"
									variant={isLocked ? 'warning' : 'info'}
									title={
										!isLocked
											? t(
													'LockTooltip',
													'Tämän jälkeen et enää voi tehdä muutoksia. Muista tallentaa liitteisiin allekirjoitettu työsopimus',
												)
											: ''
									}
									disabled={submitLock}
								>
									{submitLock && <LoadingButton />}
									{isLocked ? t('UnlockContract', 'Avaa lukitus') : t('ConfirmContract', 'Aseta työsopimus voimaan')}
								</Button>
							</Confirmation>
						)}
						<Button variant="success" onClick={() => setPdf(state)}>
							{isLocked ? t('PrintContract', 'Tulosta sopimus') : t('Preview', 'Esikatselu')}
						</Button>
						{!isLocked && (
							<Button
								variant="primary"
								onClick={handleSubmit}
								disabled={submitForm || (state.personId === '' && !state.newPerson && !state.modelName)}
							>
								{submitForm && <LoadingButton />} {t('Save', 'Tallenna')}
							</Button>
						)}
					</div>
				</Modal.Footer>
			</Modal>

			<IntegrationWarningModal
				show={showIntegrationConfirm}
				onHide={hideIntegrationModal}
				onConfirm={executePendingAction}
				integrationErrors={integrationErrors}
			/>
			<DuplicateSocSecModal
				show={socSecDuplicateWarning}
				onHide={() => setSocSecDuplicateWarning(false)}
				onConfirm={() => {
					setSocSecDuplicateWarning(false)
					proceedToSave()
				}}
			/>
		</>
	)
}
